'use client';

import { useRef } from 'react';

import { useControlledState } from 'codekit';
import { motion } from 'framer-motion';

import { cn } from 'utils/cn';

import styles from './styles.module.css';

export interface SwitcherOption<T> {
	value: T;
	label: string;
}

interface ComponentProps<T> {
	options: SwitcherOption<T>[];
	optionByValue?: T;
	width?: React.CSSProperties['width'];
	onChange?: (value: T) => void;

	containerClassName?: string;
	itemClassName?: string;
	underlineClassName?: string;
}

function Switcher<T>(props: ComponentProps<T>) {
	const switcherId = useRef(String.uuid());
	const [currentOption, setCurrentOption] = useControlledState<T | undefined>(
		props.optionByValue,
		props.options[0]?.value,
	);

	function handleChange(value: T) {
		props.onChange?.(value);
		setCurrentOption(value);
	}

	return (
		<div
			className={cn(styles.container, props.containerClassName)}
			style={{
				gridTemplateColumns: props.options.map(() => '1fr').join(' '),
				width: props.width ?? 'fit-content',
			}}
		>
			{props.options.map((option) => (
				<motion.div
					key={String(option.value)}
					className={cn(
						styles.item,
						props.itemClassName,
						props.optionByValue === option.value && styles.active,
					)}
					onClick={() => handleChange(option.value)}
				>
					<span>{option.label}</span>
					{currentOption === option.value && (
						<motion.div
							className={cn(
								styles.underline,
								props.underlineClassName,
							)}
							layoutId={'underline' + switcherId.current}
						/>
					)}
				</motion.div>
			))}
		</div>
	);
}

export default Switcher;
